import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { BrewcontrolNodeId } from "@brewbase/db/types";
import { AlertTriangleIcon } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { trpc } from "@/hooks/trpc";
import { useCan } from "@/hooks/use-operator";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { InlineEditableInput } from "@/components/InlineEditableInput";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import {
  ConnectionLabel,
  LifecycleBadge,
  type NodeLifecycle,
} from "@/features/brewcontrol/node-status";

export const Route = createFileRoute("/brewcontrol/$nodeId/")({
  component: NodeOverview,
});

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-4 py-1.5">
      <div className="w-40 shrink-0 text-xs text-muted-foreground">{label}</div>
      <div className="min-w-0 text-sm">{children}</div>
    </div>
  );
}

const NONE = <span className="text-muted-foreground">—</span>;

/** Desired versus applied, in one line, with the detail on the Config page. */
function ConfigSummary({ nodeId }: { nodeId: BrewcontrolNodeId }) {
  const { data } = trpc.brewcontrolConfig.status.useQuery(
    { nodeId },
    { refetchInterval: 10_000 },
  );
  if (!data) return NONE;
  const saved = data.scopes.filter((s) => s.desired);
  const behind = saved.filter((s) => !s.inSync).length;
  return (
    <Link
      to="/brewcontrol/$nodeId/config"
      params={{ nodeId }}
      className="hover:underline"
    >
      <span className="text-xs">
        {data.workspaces.length} workspace
        {data.workspaces.length === 1 ? "" : "s"} · {saved.length} documents
      </span>
      {behind > 0 && (
        <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-700">
          <AlertTriangleIcon className="h-3 w-3" />
          {data.reported ? `${behind} not applied` : "node has not reported"}
        </span>
      )}
    </Link>
  );
}

function NodeOverview() {
  const { nodeId } = Route.useParams();
  const canManage = useCan("brewcontrol-nodes:manage");
  const utils = trpc.useUtils();

  const { data: node } = trpc.brewcontrol.getNode.useQuery(
    { id: nodeId },
    { refetchInterval: 5000 },
  );

  const invalidate = () => {
    void utils.brewcontrol.getNode.invalidate({ id: nodeId });
    void utils.brewcontrol.listNodes.invalidate();
  };

  const approve = trpc.brewcontrol.approveNode.useMutation({
    onSuccess: () => {
      toast.success("Node approved");
      invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const revoke = trpc.brewcontrol.revokeNode.useMutation({
    onSuccess: () => {
      toast.success("Credential revoked. The node can no longer connect.");
      invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const navigate = useNavigate();
  const archive = trpc.brewcontrol.archiveNode.useMutation({
    onSuccess: () => {
      toast.success("Node archived");
      void utils.brewcontrol.listNodes.invalidate();
      void utils.brewcontrol.listArchivedNodes.invalidate();
      void navigate({ to: "/brewcontrol" });
    },
    onError: (e) => toast.error(e.message),
  });

  const restore = trpc.brewcontrol.restoreNode.useMutation({
    onSuccess: () => {
      toast.success("Node restored. Issue an enrolment token to reconnect it.");
      invalidate();
      void utils.brewcontrol.listArchivedNodes.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const rename = trpc.brewcontrol.renameNode.useMutation({
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(e.message),
  });

  const reissue = trpc.brewcontrol.reissueEnrolmentToken.useMutation({
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(e.message),
  });

  if (!node) {
    return (
      <div className="px-6 py-6 text-sm text-muted-foreground">
        Loading node…
      </div>
    );
  }

  const status = node.status as NodeLifecycle;
  const archived = node.deleted_at !== null;

  return (
    <div className="max-w-3xl px-6 py-6 space-y-8">
      {archived && (
        <div className="flex items-center justify-between gap-4 bg-olive-3 px-3 py-2 text-sm text-olive-11">
          <span>
            Archived {formatDistanceToNow(node.deleted_at!, { addSuffix: true })}.
            Its runs and telemetry are kept; it can no longer connect.
          </span>
          {canManage && (
            <Button
              variant="ghost"
              disabled={restore.isPending}
              onClick={() => restore.mutate({ id: nodeId })}
            >
              Restore
            </Button>
          )}
        </div>
      )}
      <section className="space-y-1">
        <h2 className="text-sm font-medium">Connection</h2>
        <div className="divide-y divide-olive-5 border-t">
          <Field label="State">
            <ConnectionLabel connected={node.connected} />
          </Field>
          <Field label="Approval">
            <LifecycleBadge status={status} />
          </Field>
          <Field label="Connected since">
            {node.connected && node.connectedAt
              ? formatDistanceToNow(node.connectedAt, { addSuffix: true })
              : node.last_disconnected_at
                ? `disconnected ${formatDistanceToNow(node.last_disconnected_at, { addSuffix: true })}`
                : "never connected"}
          </Field>
          <Field label="Last uplink">
            {node.lastUplinkAt
              ? formatDistanceToNow(node.lastUplinkAt, { addSuffix: true })
              : NONE}
          </Field>
        </div>
      </section>

      <section className="space-y-1">
        <h2 className="text-sm font-medium">Identity</h2>
        <div className="divide-y divide-olive-5 border-t">
          <Field label="Name">
            {canManage ? (
              <InlineEditableInput
                value={node.display_name}
                onSave={(v) => {
                  const displayName = v.trim();
                  if (displayName) rename.mutate({ id: nodeId, displayName });
                }}
              />
            ) : (
              node.display_name
            )}
          </Field>
          <Field label="Hostname">
            <span className="font-mono text-xs">{node.hostname ?? NONE}</span>
          </Field>
          <Field label="Machine ID">
            <span className="font-mono text-xs">{node.machine_id ?? NONE}</span>
          </Field>
          <Field label="Agent version">
            <span className="font-mono text-xs">
              {node.agent_version ?? NONE}
            </span>
          </Field>
        </div>
      </section>

      <section className="space-y-1">
        <h2 className="text-sm font-medium">Running</h2>
        <div className="divide-y divide-olive-5 border-t">
          <Field label="Run">
            <span className="font-mono text-xs">
              {node.runState?.bcRunId ?? NONE}
            </span>
          </Field>
          <Field label="State">{node.runState?.state ?? NONE}</Field>
          <Field label="Config">
            <ConfigSummary nodeId={nodeId} />
          </Field>
        </div>
      </section>

      {canManage && !archived && (
        <section className="space-y-3">
          <h2 className="text-sm font-medium">Administration</h2>

          <div className="flex flex-wrap gap-2">
            {status === "pending" && (
              <Button
                onClick={() => approve.mutate({ id: nodeId })}
                disabled={approve.isPending}
              >
                Approve node
              </Button>
            )}
            <Button
              variant="ghost"
              disabled={reissue.isPending}
              onClick={() => reissue.mutate({ id: nodeId })}
            >
              Issue new enrolment token
            </Button>
            <DeleteConfirmDialog
              title="Revoke this node?"
              description="Its credential stops working immediately and it will be disconnected. Recovery requires provisioning a fresh enrolment token onto the machine."
              onConfirm={() => revoke.mutate({ id: nodeId })}
              trigger={<Button variant="destructive">Revoke credential</Button>}
            />
            <DeleteConfirmDialog
              title="Archive this node?"
              description="Use this for a machine that is gone for good. The node leaves the fleet, its credential stops working, and its workspaces are retired with their vessels unbound. Runs and telemetry stay. Not possible while a run is open."
              onConfirm={() => archive.mutate({ id: nodeId })}
              trigger={<Button variant="destructive">Archive node</Button>}
            />
          </div>

          {reissue.data && (
            <div className="border border-dashed bg-muted/40 p-4 space-y-2">
              <div className="text-sm font-medium">New enrolment token</div>
              <p className="text-xs text-muted-foreground">
                Shown once. Place it at
                <span className="font-mono">
                  {" "}
                  /etc/brewcontrol/enrolment-token{" "}
                </span>
                on the node.
              </p>
              <Textarea
                readOnly
                rows={2}
                className="font-mono text-xs"
                value={reissue.data.enrolmentToken}
              />
            </div>
          )}
        </section>
      )}
    </div>
  );
}
